import React from "react";
import clsx from "clsx";
import Input from "../input";
import List from "../list";
import {
  AiFill,
  List as ListIcon,
  Share,
  Coin,
  Cs,
  InfoInfo,
  Logout,
  InfoQuestion,
  Delete,
  Document,
  Report,
} from "../../assets/svgs/icons";

// 아이콘 이미지 URL (Figma에서 추출)

type OverlayPopoverType = "like" | "dislike" | "myAiR" | "edit";

interface OverlayPopoverProps {
  type?: OverlayPopoverType;
  // like/dislike 타입을 위한 props
  feedback?: string;
  onFeedbackChange?: (value: string) => void;
  onSave?: (feedback: string) => void;
  onCancel?: () => void;
}

const OverlayPopover: React.FC<OverlayPopoverProps> = ({
  type = "like",
  feedback = "",
  onFeedbackChange,
  onSave,
  onCancel,
}) => {
  if (type === "myAiR") {
    return (
      <div className="bg-white flex flex-col items-center min-w-[288px] p-4 rounded-[20px] shadow-lg w-[288px]">
        <div className="flex flex-col w-full gap-3 mb-2">
          {/* Vertical List */}
          <div className="flex gap-3 justify-between">
            <List
              icon={<Report className="w-5 h-5" />}
              label="나의 활동"
              style="vertical"
              type="link"
            />
            <List
              icon={<AiFill className="w-5 h-5" />}
              label="어투 설정"
              style="vertical"
              type="link"
            />
            <List
              icon={<ListIcon className="w-5 h-5" />}
              label="구매 내역"
              style="vertical"
              type="link"
            />
          </div>
        </div>
        {/* Horizontal List */}
        <div className="flex flex-col w-full gap-2">
          <List
            icon={<Share className="w-5 h-5" />}
            label="공유 목록"
            style="horizontal"
            type="link"
          />
          <List
            icon={<Coin className="w-5 h-5" />}
            label="구독 상품"
            style="horizontal"
            type="link"
          />
          <List
            icon={<Cs className="w-5 h-5" />}
            label="고객 센터"
            style="horizontal"
            type="link"
          />
          <List
            icon={<InfoInfo className="w-5 h-5" />}
            label="회사 정보"
            style="horizontal"
            type="link"
          />
          {/* Action List */}
          <List
            icon={<Logout className="w-5 h-5" />}
            label="로그아웃"
            style="horizontal"
          />
        </div>
      </div>
    );
  }
  if (type === "edit") {
    return (
      <div className="bg-white flex flex-col items-center rounded-[8px] shadow-lg p-2 min-w-[120px]">
        <List
          icon={<InfoQuestion className="w-5 h-5" />}
          label="제목 변경"
          style="horizontal"
          type="link"
        />
        <List
          icon={<Delete className="w-5 h-5" />}
          label="삭제 하기"
          style="horizontal"
          type="link"
        />
        <List
          icon={<Document className="w-5 h-5" />}
          label="이력 보관"
          style="horizontal"
          type="link"
        />
      </div>
    );
  }
  // like/dislike type
  const MAX_FEEDBACK_LENGTH = 100;
  const isExceedingLimit = feedback.length > MAX_FEEDBACK_LENGTH;
  const questionText =
    type === "like"
      ? "어떤점이 마음에 드셨나요?"
      : "어떤점이 마음에 안드셨나요?";

  const handleSave = () => {
    if (isExceedingLimit) return;
    onSave?.(feedback);
  };

  const handleCancel = () => {
    onCancel?.();
  };

  return (
    <div className="bg-white box-border content-stretch flex flex-col gap-[12px] items-end min-w-[260px] p-[16px] relative rounded-bl-[20px] rounded-br-[20px] rounded-tr-[20px] shadow-[0px_6px_12px_-6px_rgba(0,0,0,0.12),0px_8px_24px_-4px_rgba(0,0,0,0.08)] shrink-0 w-full">
      <div className="flex flex-col font-['Pretendard',sans-serif] font-medium justify-center leading-[0] min-w-full not-italic relative shrink-0 text-[#212121] text-[14px] tracking-[-0.28px] w-[min-content]">
        <p className="leading-[20px]">{questionText}</p>
      </div>

      <div className="w-full">
        <Input
          type="multiline"
          size="md"
          value={feedback}
          onChange={(value) => onFeedbackChange?.(value as string)}
          helperText={
            isExceedingLimit
              ? "⚠️ 최대 100글자까지만 입력이 가능합니다."
              : `${feedback.length}/${MAX_FEEDBACK_LENGTH}`
          }
          label=""
        />
      </div>

      <div className="content-stretch flex gap-[8px] items-start relative shrink-0">
        <button
          onClick={handleCancel}
          className="border border-[#eeeeee] border-solid h-[32px] px-[12px] relative rounded-[4px] shrink-0 hover:bg-gray-50 transition-colors"
        >
          <span className="font-['Pretendard',sans-serif] font-medium text-[#666666] text-[14px] text-center whitespace-pre leading-[20px]">
            취소
          </span>
        </button>
        <button
          onClick={handleSave}
          disabled={!feedback.trim() || isExceedingLimit}
          className={clsx(
            "box-border content-stretch flex h-[32px] items-center justify-center overflow-clip px-[12px] py-0 relative rounded-[4px] shrink-0 transition-colors",
            feedback.trim() && !isExceedingLimit
              ? "bg-[#5545e4] hover:bg-[#4939d0]"
              : "bg-[#eeeeee]",
          )}
        >
          <span
            className={clsx(
              "font-['Pretendard',sans-serif] font-regular text-[14px] text-center whitespace-pre leading-[20px]",
              feedback.trim() && !isExceedingLimit
                ? "text-white"
                : "text-[#888888]",
            )}
          >
            저장
          </span>
        </button>
      </div>
    </div>
  );
};

export default OverlayPopover;
